import { useMemo } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useAppAbility } from "@/casl";
import { Icon, Tooltip, Typography } from "@/components/atoms";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { useLayout, useOperator, useProfile, useSiteConfig } from "@/hooks";
import { clsx } from "@/utils";
import { getNavigation } from "./helpers";
import { Tab } from "./types";

interface SidebarNavProps {
  ignoreSidebarOpen?: boolean;
};

export const SidebarNav = ({ ignoreSidebarOpen }: SidebarNavProps) => {
  const { id: operatorId, roles } = useOperator();
  const { id: userId } = useProfile();
  const { layout: { sidebarOpen: isOpen } } = useLayout();
  const sidebarOpen = ignoreSidebarOpen ? true : isOpen;
  const headerClassName = clsx("my-3 text-neutral-dark-gray", {
    "text-center": !sidebarOpen,
  });

  const renderDriverMenu = () => {
    const navItem = {
      name: "My Profile",
      to: `/driver/${userId}/profile/`,
      icon: "User",
    };

    if (roles.includes("Driver")) {
      return (
        <>
          <Typography variant="action" className={headerClassName}>
            Driver Menu
          </Typography>
          <SidebarNavItem key={navItem.name} item={navItem} sidebarOpen={sidebarOpen} />
        </>
      );
    }
  };

  const renderClientMenu = () => {
    const navItem = {
      name: "My Bookings",
      to: `/passenger/${userId}/booking/history`,
      icon: "ArchiveBook",
    };

    if (roles.includes("Passenger")) {
      return (
        <>
          <Typography variant="action" className={headerClassName}>
            Client Menu
          </Typography>
          <div className="flex flex-col">
            <SidebarNavItem key={navItem.name} item={navItem} sidebarOpen={sidebarOpen} />
          </div>
        </>
      );
    }
  };

  return (
    <nav className="flex flex-col no-scrollbar overflow-auto border-none mt-3 px-5">
      {renderDriverMenu()}
      {renderClientMenu()}
      <Typography variant="action" className={headerClassName}>
        Menu
      </Typography>
      <div className="flex flex-col">
        {getNavigation(roles, userId, operatorId).map((item) => (
          <SidebarNavItem key={item.name} item={item} sidebarOpen={sidebarOpen} />
        ))}
      </div>
    </nav>
  );
};

const SidebarNavItem = ({ item, sidebarOpen }: { item: Tab; sidebarOpen: boolean }) => {
  const ability = useAppAbility();
  const phpHostUrl = getPhpHostUrl();
  const { operatorId: hasOperatorId } = useParams();
  const { id: operatorId } = useOperator();
  const { domain } = useSiteConfig();
  const baseClass = clsx("flex items-center px-2.5 py-3.5 rounded-md hidden", {
    "block": item.permission ? ability.can(item.permission[0], item.permission[1]) : false,
  });

  const getClassName = ({ isActive }: { isActive: boolean }) => {
    return clsx(baseClass, {
      "bg-primary-light": isActive,
      "hover:bg-neutral-gray hover:text-neutral-black": !isActive,
    });
  };

  //todo: update when bookings is released
  const updatedBookingItem = useMemo(() => {
    if (item.name !== "Bookings") return item;

    if (operatorId === "pro-e64e72cb-79b1-4bda-a222-030cb7a3f64d" || operatorId === "pro-b0444dc8-4b5f-4233-b54a-21e0f7ffd67f") {
      return {
        ...item,
        to: "/bookings",
        isDashboard: true,
      };
    } else return item;
  }, [item, operatorId]);

  const navItem = (
    <span className={clsx("flex w-full gap-x-3 text-neutral-black", { "justify-center": !sidebarOpen })}>
      <Icon
        name={updatedBookingItem.icon}
        className="flex-shrink-0"
        aria-hidden="true"
      />
      <Typography variant="action" className={clsx({ "hidden": !sidebarOpen })}>
        {updatedBookingItem.name}
      </Typography>
    </span>
  );

  const basePath = hasOperatorId ? `/operator/${operatorId}` : "";

  if (updatedBookingItem.isDashboard) {
    return (
      <Tooltip content={updatedBookingItem.name} placement="right" disabled={sidebarOpen}>
        <NavLink to={`${basePath}${updatedBookingItem.to}`} className={getClassName}>
          {navItem}
        </NavLink>
      </Tooltip>
    );
  }

  //TODO: Remove this when we have a proper way to handle this
  if (updatedBookingItem.to === "/booking-admin" && domain === "cod") {
    return (
      <Tooltip content={updatedBookingItem.name} placement="right" disabled={sidebarOpen}>
        <NavLink to={`${basePath}/bookings`} className={getClassName}>
          {navItem}
        </NavLink>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={updatedBookingItem.name} placement="right" disabled={sidebarOpen}>
      <a href={`${phpHostUrl}${item.to}`} className={clsx(baseClass, "hover:bg-neutral-gray hover:text-neutral-black")}>
        {navItem}
      </a>
    </Tooltip>
  );
};